import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../components/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import { useFelipe } from "../hooks/useFelipe";
import FelipeImg from "../assets/Felipe_2-removebg.png";
import ActionModal from "../components/felipe/ActionModal";

// Tipografía de visores: +2px sobre el diseño base.
const VISOR_FS_BUMP = 2;
const vfs = (px) => (Number.isFinite(px) ? px + VISOR_FS_BUMP : px);

// ── PALETA ───────────────────────────────────────────────────
const BLUE        = "#3d6fff";
const STATUS_COLOR = { ok: "#22c55e", warn: "#eab308", error: "#ef4444", pending: "#64748b", sleep: "#f97316", partial: "#ef4444" };
const STATUS_BG    = { ok: "rgba(34,197,94,0.12)", warn: "rgba(234,179,8,0.12)", error: "rgba(239,68,68,0.12)", pending: "rgba(100,116,139,0.1)", sleep: "rgba(249,115,22,0.15)", partial: "rgba(239,68,68,0.12)" };
const STATUS_ES    = { ok: "OK", warn: "Atención", error: "Error", pending: "Próximo", sleep: "Dormido", partial: "Incompleto" };

function cuandoActuacion(iso) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return "";
  const min = (Date.now() - t) / 60000;
  let relativo = "ahora";
  if (min >= 1 && min < 60) relativo = `hace ${Math.round(min)} min`;
  else if (min >= 60) {
    const h = min / 60;
    relativo = h < 48 ? `hace ${Math.round(h)} h` : `hace ${Math.round(h / 24)} días`;
  }
  const parts = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const g = (tipo) => parts.find((p) => p.type === tipo)?.value || "";
  return `${relativo} · ${g("day")}/${g("month")} a las ${g("hour")}:${g("minute")}`;
}

// FirmaX — servicio próximo
const FIRMX_CARD = {
  id: "firmex", label: "FirmaX", category: "Firma digital",
  icon: "✍️", status: "pending", value: "Próximo", detail: "Integración en desarrollo",
  metrics: [
    { label: "Saldo", value: "—", status: "pending", periodo: true },
    { label: "Hoy", value: "—", status: "pending", periodo: true },
    { label: "Semana", value: "—", status: "pending", periodo: true },
    { label: "Mes", value: "—", status: "pending", periodo: true },
    { label: "Año", value: "—", status: "pending", periodo: true },
    { label: "Estado", value: "En desarrollo", status: "pending" },
  ],
  history: new Array(12).fill(50),
};

const FIX_ACTIONS_MAP = {
  elevenlabs:   { id: "browser_login_regenerate_key", label: "🤖 Regenerar key (automático)", icon: "🤖", risk: "medium", params: [{ name: "service", label: "Servicio", type: "text", placeholder: "elevenlabs", defaultValue: "elevenlabs" }] },
  telnyx:       { id: "browser_login_regenerate_key", label: "🤖 Regenerar key (automático)", icon: "🤖", risk: "medium", params: [{ name: "service", label: "Servicio", type: "text", placeholder: "telnyx", defaultValue: "telnyx" }] },
  anthropic:    { id: "update_anthropic_key",          label: "🔑 Actualizar key Anthropic",  icon: "🔑", risk: "medium", params: [{ name: "api_key", label: "Nueva API Key", type: "password", placeholder: "sk-ant-..." }] },
  openai:       { id: "update_openai_key",             label: "🔑 Actualizar key OpenAI",     icon: "🔑", risk: "medium", params: [{ name: "api_key", label: "Nueva API Key", type: "password", placeholder: "sk-..." }] },
  db:           { id: "vacuum_db",                     label: "🧹 Optimizar BD",             icon: "🧹", risk: "low",    params: [] },
  railway:      { id: "cleanup_old_records",           label: "🗑️ Liberar memoria",           icon: "🗑️", risk: "low",    params: [] },
  smtp:         { id: "test_smtp_agent",               label: "📧 Verificar SMTP",           icon: "📧", risk: "low",    params: [{ name: "agent_name", label: "Agente", type: "text", placeholder: "Josefa M." }] },
  autoenhanced: { id: "update_autoenhanced_key",       label: "🔑 Actualizar key AutoEnhance",icon: "🔑", risk: "medium", params: [{ name: "api_key", label: "Nueva API Key", type: "password", placeholder: "api_key..." }] },
  wa_business:  { id: "update_env_var",                label: "🔑 Actualizar Token Meta",    icon: "🔑", risk: "medium", params: [{ name: "key", type: "text", placeholder: "META_WHATSAPP_TOKEN" }, { name: "value", label: "Token", type: "password", placeholder: "EAA..." }] },
};

// ── SPARKLINE ────────────────────────────────────────────────
function Sparkline({ history = [], color = "#22c55e" }) {
  const w = 56, h = 16;
  if (!history?.length) return null;
  const pts = history.map((v, i) => {
    const x = (i / Math.max(history.length - 1, 1)) * w;
    const y = h - (v / 100) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block", flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ── MONITOR CARD ─────────────────────────────────────────────
function MonitorCard({ mon, isDark, onFix, onHistory, fill }) {
  const color   = STATUS_COLOR[mon.status] || "#64748b";
  const bright  = mon.expand || fill;
  const bg      = bright
    ? (isDark ? "#181c24" : "#ffffff")
    : (isDark ? "#0d1017" : "#f8fafc");
  const borderColor = isDark ? "#3a4358" : "#c8c8be";
  const border  = bright
    ? `1px solid ${borderColor}`
    : `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)"}`;
  const text    = isDark ? "#e2e8f0" : "#0f172a";
  const muted   = isDark ? "#475569" : "#94a3b8";
  const titleColor = bright ? "#facc15" : text;
  const labelColor = bright ? "#ffffff" : muted;
  const bodyColor  = bright ? "#ffffff" : text;
  const fixAct  = FIX_ACTIONS_MAP[mon.id];
  // AutoEnhance / SMTP: Atención ≠ hay que “reparar” (créditos, corte del correo…).
  // Solo Reparar si el estado es error real (clave inválida, etc.).
  const needsFix = (mon.id === "autoenhanced" || mon.id === "smtp")
    ? mon.status === "error"
    : (mon.status === "error" || mon.status === "warn");
  const tieneActuaciones = (mon.acciones || []).some((a) => a.queHizo || (a.texto && a.texto !== "Sin actuaciones"));

  return (
    <div
      onClick={() => onHistory(mon)}
      style={{
        background: bg, border, borderRadius: 10,
        borderLeft: `3px solid ${color}`,
        padding: "10px 12px",
        cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 4,
        transition: "box-shadow .15s",
        minHeight: 0,
        height: bright ? "100%" : undefined,
        overflow: bright ? "hidden" : undefined,
        boxShadow: bright
          ? `0 1px 3px ${isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)"}`
          : undefined,
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = bright
        ? `0 4px 16px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.1)"}, 0 0 0 1px ${color}40`
        : `0 2px 12px ${color}33`}
      onMouseLeave={e => e.currentTarget.style.boxShadow = bright
        ? `0 1px 3px ${isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)"}`
        : "none"}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span style={{ fontSize: vfs(15) }}>{mon.icon}</span>
        <span style={{ fontSize: vfs(11), fontWeight: 700, color: titleColor, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {mon.label}
        </span>
        <span style={{
          fontSize: vfs(9), fontWeight: 700, color,
          background: STATUS_BG[mon.status], borderRadius: 4,
          padding: "1px 5px", flexShrink: 0,
        }}>
          {STATUS_ES[mon.status]}
        </span>
      </div>

      {/* Valor + sparkline */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: vfs(12), fontWeight: 600, color, lineHeight: 1.2 }}>{mon.value}</div>
          <div style={{ fontSize: vfs(9), color: labelColor, lineHeight: 1.3 }}>{mon.detail}</div>
          {mon.queEs && (
            <div style={{ fontSize: vfs(9), color: labelColor, lineHeight: 1.35, marginTop: 2, whiteSpace: "normal" }}>{mon.queEs}</div>
          )}
        </div>
        <Sparkline history={mon.history} color={color} />
      </div>

      {/* Métricas: periodos Saldo/Hoy/Semana/Mes/Año primero; en expandidos se listan todas */}
      <div style={bright ? { flex: 1, minHeight: 0, overflowY: "auto" } : undefined}>
      {(() => {
        const all = Array.isArray(mon.metrics) ? mon.metrics : [];
        const periodos = all.filter((m) => m && m.periodo);
        const resto = all.filter((m) => m && !m.periodo);
        const ordered = periodos.length ? [...periodos, ...resto] : all;
        const shown = bright
          ? (mon.metricLimit != null ? ordered.slice(0, mon.metricLimit) : ordered)
          : ordered.slice(0, mon.metricLimit || Math.max(5, periodos.length || 3));
        return shown.map((m, i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4,
          padding: "1px 0",
          borderTop: i === 0 || (periodos.length && i === periodos.length)
            ? `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.05)"}`
            : "none",
        }}>
          <span style={{
            fontSize: vfs(9),
            color: m.grupo || m.periodo ? bodyColor : labelColor,
            fontWeight: m.grupo || (m.periodo && ['Saldo', 'Quedan', 'Créditos', 'Mes (USD)'].includes(m.label)) ? 700 : 400,
            marginTop: m.grupo && i > 0 ? 4 : 0,
            flexShrink: 0,
          }}>{m.label}</span>
          {m.aviso ? (
            <span style={{
              fontSize: vfs(8), fontWeight: 700,
              color: m.aviso === "reintentando" ? "#f97316" : (STATUS_COLOR.error || "#ef4444"),
              flex: 1, textAlign: "center", minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {m.aviso}
            </span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
          <span style={{
            fontSize: vfs(9), fontWeight: 600, color: STATUS_COLOR[m.status] || muted,
            flexShrink: 1, minWidth: 0, textAlign: 'right', whiteSpace: 'normal', lineHeight: 1.25,
          }}>{m.value}</span>
        </div>
        ));
      })()}
      </div>

      {bright && (
        <div style={{
          flexShrink: 0,
          marginTop: 4,
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          paddingTop: 4,
          height: 20,
          boxSizing: "border-box",
          overflow: "hidden",
        }}>
          <div style={{ fontSize: vfs(9), fontWeight: 700, color: "#ffffff", lineHeight: `${vfs(9) + 4}px` }}>
            {tieneActuaciones ? "Con actuaciones" : "Sin actuaciones"}
          </div>
        </div>
      )}

      {/* Botón fix */}
      {needsFix && fixAct && (
        <button
          onClick={e => { e.stopPropagation(); onFix(mon, fixAct); }}
          style={{
            marginTop: 4,
            background: `${color}22`, color, border: `1px solid ${color}44`,
            borderRadius: 6, padding: "3px 8px", fontSize: vfs(10), fontWeight: 600,
            cursor: "pointer", textAlign: "center",
          }}
        >
          ⚡ Reparar
        </button>
      )}
    </div>
  );
}

// ── HISTORY DRAWER (inferior) ────────────────────────────────
function HistoryDrawer({ mon, isDark, onClose, onInvestigate }) {
  const [history, setHistory]       = useState(null);
  const [loadingAI, setLoadingAI]   = useState(false);

  useEffect(() => {
    if (!mon) return;
    setHistory(null);
    const token = localStorage.getItem("token");
    fetch(`/api/felipe/monitor/${mon.id}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { status_history: [], actions: [] })
      .then(data => setHistory(data))
      .catch(() => {});
  }, [mon?.id]);

  if (!mon) return null;
  const bg     = isDark ? "#0d1017" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.1)";
  const text   = isDark ? "#e2e8f0" : "#0f172a";
  const muted  = isDark ? "#64748b" : "#94a3b8";
  const color  = STATUS_COLOR[mon.status] || "#64748b";

  const actos = (mon.acciones || []).filter((a) => a.queHizo || (a.texto && a.texto !== "Sin actuaciones"));

  const panel = (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 80,
      background: isDark ? "#0d1017" : "#fff",
      borderTop: `3px solid ${color}`,
      boxShadow: "0 -8px 32px rgba(0,0,0,0.35)",
      height: "70vh",
      minHeight: 480,
      overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
        <span style={{ fontSize: vfs(18) }}>{mon.icon}</span>
        <span style={{ fontWeight: 700, color: text, fontSize: vfs(14) }}>{mon.label}</span>
        <span style={{ fontSize: vfs(10), background: STATUS_BG[mon.status], color, borderRadius: 4, padding: "2px 8px", fontWeight: 700 }}>
          {STATUS_ES[mon.status]}
        </span>
        <span style={{ fontSize: vfs(11), color: muted, flex: 1 }}>{mon.detail}</span>
        <button
          onClick={() => { setLoadingAI(true); onInvestigate(mon).finally(() => setLoadingAI(false)); }}
          style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: vfs(11), cursor: "pointer", marginRight: 8 }}
        >
          {loadingAI ? "⏳ Analizando..." : "🔍 Felipe investiga"}
        </button>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", color: muted, fontSize: vfs(18), cursor: "pointer", padding: "2px 6px" }}
        >×</button>
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={{ fontSize: vfs(11), fontWeight: 700, color: muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
            Qué he hecho
          </div>
          {actos.length === 0 ? (
            <p style={{ fontSize: vfs(15), color: text, lineHeight: 1.5, margin: 0 }}>
              Todavía no he tenido que actuar en este visor.
            </p>
          ) : (
            actos.map((a, i) => (
              <div key={i} style={{
                padding: "12px 0",
                borderTop: i === 0 ? "none" : `1px solid ${border}`,
              }}>
                {a.queHizo ? (
                  <>
                    <div style={{ fontSize: vfs(16), fontWeight: 700, color: text }}>{a.titulo}</div>
                    <div style={{ fontSize: vfs(15), color: text, lineHeight: 1.5, marginTop: 6 }}>{a.queHizo}</div>
                    <div style={{ fontSize: vfs(15), color: a.ok === false ? STATUS_COLOR.error : STATUS_COLOR.ok, lineHeight: 1.5, marginTop: 4 }}>
                      Resultado: {a.resultado}
                    </div>
                    {a.hace && (
                      <div style={{ fontSize: vfs(13), color: muted, marginTop: 4 }}>{a.hace}</div>
                    )}
                    {!a.hace && a.at && (
                      <div style={{ fontSize: vfs(13), color: muted, marginTop: 4 }}>{cuandoActuacion(a.at)}</div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: vfs(15), color: text, lineHeight: 1.5 }}>{a.texto}</div>
                )}
              </div>
            ))
          )}
        </div>
        {history?.actions?.length > 0 && !(mon.acciones || []).some((a) => a.queHizo) && (
          <div>
            {history.actions.map((a, i) => (
              <div key={i} style={{ fontSize: vfs(14), color: text, lineHeight: 1.45, padding: "6px 0" }}>
                <div>{a.message || a.action_id}</div>
                {a.executed_at && (
                  <div style={{ fontSize: vfs(13), color: muted, marginTop: 4 }}>{cuandoActuacion(a.executed_at)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

// ── FIX CARD (propuestos por Felipe) ─────────────────────────
function FixCard({ fix, isDark, onApprove, onReject, loading }) {
  const bg     = isDark ? "#0d1017" : "#fff8ec";
  const border = isDark ? "rgba(234,179,8,0.25)" : "rgba(234,179,8,0.4)";
  const text   = isDark ? "#e2e8f0" : "#0f172a";
  const muted  = isDark ? "#64748b" : "#94a3b8";

  return (
    <div style={{
      background: bg, border: `1px solid ${border}`,
      borderLeft: "3px solid #eab308",
      borderRadius: 8, padding: "10px 14px",
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: vfs(13) }}>🔧</span>
        <span style={{ fontSize: vfs(12), fontWeight: 700, color: text, flex: 1 }}>{fix.title || fix.fix_title}</span>
        <span style={{ fontSize: vfs(9), color: "#eab308", background: "rgba(234,179,8,0.12)", borderRadius: 4, padding: "1px 6px", fontWeight: 700 }}>
          PENDIENTE
        </span>
      </div>
      {fix.root_cause && (
        <div style={{ fontSize: vfs(10), color: muted, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 4, padding: "4px 8px" }}>
          <b style={{ color: text }}>Causa:</b> {fix.root_cause}
        </div>
      )}
      <div style={{ fontSize: vfs(10), color: muted }}>{fix.description || fix.fix_description}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => onApprove(fix.fix_id || fix.id)}
          disabled={loading}
          style={{
            background: "#22c55e", color: "#fff", border: "none",
            borderRadius: 6, padding: "4px 14px", fontSize: vfs(11), fontWeight: 700,
            cursor: loading ? "wait" : "pointer", flex: 1,
          }}
        >
          ✓ Aprobar
        </button>
        <button
          onClick={() => onReject(fix.fix_id || fix.id)}
          disabled={loading}
          style={{
            background: "none", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)",
            borderRadius: 6, padding: "4px 14px", fontSize: vfs(11), fontWeight: 700,
            cursor: loading ? "wait" : "pointer", flex: 1,
          }}
        >
          ✗ Rechazar
        </button>
      </div>
    </div>
  );
}

// ── LOG ROW ──────────────────────────────────────────────────
const LOG_COLOR = { error: "#ef4444", warn: "#eab308", info: "#3d6fff" };

function LogRow({ log, isDark }) {
  const c = LOG_COLOR[log.level] || "#64748b";
  const t = isDark ? "#94a3b8" : "#64748b";
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "3px 0", fontSize: vfs(11), borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.04)"}` }}>
      <span style={{ color: c, fontWeight: 700, flexShrink: 0, minWidth: 36 }}>
        {log.level === "error" ? "ERR" : log.level === "warn" ? "WRN" : "INF"}
      </span>
      <span style={{ color: t, flexShrink: 0, minWidth: 42 }}>
        {new Date(log.ts).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
      </span>
      <span style={{ color: isDark ? "#e2e8f0" : "#0f172a", flex: 1, wordBreak: "break-word" }}>{log.msg}</span>
    </div>
  );
}

// ── CHAT BUBBLE ──────────────────────────────────────────────
function Burbuja({ msg, isDark, onAction, onActionWithParams }) {
  const isUser = msg.role === "user";
  const bg  = isUser ? BLUE : (isDark ? "#1e293b" : "#f1f5f9");
  const col = isUser ? "#fff" : (isDark ? "#e2e8f0" : "#0f172a");

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 8 }}>
      <div style={{ maxWidth: "80%", background: bg, borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px", padding: "8px 12px", color: col, fontSize: vfs(13) }}>
        <div style={{ whiteSpace: "pre-wrap" }}>{msg.content}</div>
        {msg.actions?.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {msg.actions.map((a, i) => (
              <button
                key={i}
                onClick={() => a.params?.length ? onActionWithParams(a) : onAction(a.id, {})}
                style={{
                  background: a.risk === "low" ? "#22c55e" : a.risk === "high" ? "#ef4444" : BLUE,
                  color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px",
                  fontSize: vfs(12), fontWeight: 600, cursor: "pointer", textAlign: "left",
                }}
              >
                {a.icon || "⚡"} {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MODAL CREDENCIALES — dar todas de una vez ─────────────────
const SERVICES_CREDS = [
  { id: "elevenlabs",   label: "ElevenLabs",    icon: "🔊", emailPlaceholder: "tu@email.com",  passPlaceholder: "contraseña de elevenlabs.io" },
  { id: "telnyx",       label: "Telnyx",        icon: "📞", emailPlaceholder: "tu@email.com",  passPlaceholder: "contraseña de telnyx.com" },
  { id: "anthropic",    label: "Anthropic",     icon: "🧠", emailPlaceholder: "tu@email.com",  passPlaceholder: "contraseña de anthropic.com" },
  { id: "autoenhanced", label: "AutoEnhanced",  icon: "✨", emailPlaceholder: "tu@email.com",  passPlaceholder: "contraseña de autoenhanced.com" },
  { id: "scraper",      label: "ScraperAPI",    icon: "🕷️", emailPlaceholder: "tu@email.com",  passPlaceholder: "contraseña de scraperapi.com" },
];

function CredentialsModal({ isDark, onClose, onSave, loading }) {
  const [creds, setCreds] = useState(() =>
    Object.fromEntries(SERVICES_CREDS.map(s => [s.id, { email: "", password: "" }]))
  );
  const [show, setShow] = useState({});
  const bord = "rgba(255,255,255,0.08)";

  const handleSave = async () => {
    const filled = SERVICES_CREDS.filter(s => creds[s.id].email.trim() && creds[s.id].password.trim());
    if (!filled.length) return;
    onSave(filled.map(s => ({ service: s.id, ...creds[s.id] })));
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "#0d1017", border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 16, padding: "28px 32px", maxWidth: 600, width: "100%",
        maxHeight: "85vh", overflowY: "auto",
        boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: vfs(17), color: "#e2e8f0" }}>🔐 Credenciales de servicios</div>
            <div style={{ fontSize: vfs(12), color: "#64748b", marginTop: 4 }}>
              Rellena los que quieras. Felipe los guardará cifrados y los usará de forma autónoma.
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: vfs(20), cursor: "pointer" }}>×</button>
        </div>

        {SERVICES_CREDS.map(s => (
          <div key={s.id} style={{
            marginBottom: 14, padding: "14px", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)",
          }}>
            <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: vfs(13), marginBottom: 10 }}>
              {s.icon} {s.label}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <input
                type="email" placeholder={s.emailPlaceholder}
                value={creds[s.id].email}
                onChange={e => setCreds(prev => ({ ...prev, [s.id]: { ...prev[s.id], email: e.target.value } }))}
                style={{ flex: 1, background: "#12151e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "7px 10px", fontSize: vfs(12), color: "#e2e8f0", outline: "none" }}
              />
              <div style={{ position: "relative", flex: 1 }}>
                <input
                  type={show[s.id] ? "text" : "password"} placeholder={s.passPlaceholder}
                  value={creds[s.id].password}
                  onChange={e => setCreds(prev => ({ ...prev, [s.id]: { ...prev[s.id], password: e.target.value } }))}
                  style={{ width: "100%", boxSizing: "border-box", background: "#12151e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 7, padding: "7px 32px 7px 10px", fontSize: vfs(12), color: "#e2e8f0", outline: "none" }}
                />
                <button
                  type="button" onClick={() => setShow(p => ({ ...p, [s.id]: !p[s.id] }))}
                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: vfs(13), color: "#475569" }}
                >{show[s.id] ? "🙈" : "👁️"}</button>
              </div>
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 0", color: "#64748b", fontSize: vfs(13), cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={loading} style={{
            flex: 2, background: BLUE, border: "none", borderRadius: 8, padding: "9px 0",
            color: "#fff", fontSize: vfs(13), fontWeight: 700, cursor: loading ? "wait" : "pointer",
          }}>
            {loading ? "⏳ Guardando..." : "💾 Guardar credenciales"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PANEL CHAT (con imagen de Felipe) ────────────────────────
function FelipeChat({ chat, isDark, chatLoading, sendMessage, executeAction, startActionWithParams, userName, connected, onOpenCreds }) {
  const [input, setInput]   = useState("");
  const chatEndRef           = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const bord = "rgba(255,255,255,0.08)";

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>

      {/* ── IMAGEN FELIPE (izquierda, 50%) ── */}
      <div style={{
        width: "50%", flexShrink: 0, position: "relative",
        background: "linear-gradient(135deg, #070b12 0%, #0f172a 50%, #07111e 100%)",
        overflow: "hidden",
        borderRight: "1px solid rgba(255,255,255,0.07)",
      }}>
        <img
          src={FelipeImg} alt="Felipe"
          style={{
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "top center",
          }}
        />
        {/* Badge estado */}
        <div style={{
          position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.12)",
          padding: "5px 18px", borderRadius: 20,
          fontSize: vfs(11), fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
          color: connected ? "#22c55e" : "#ef4444",
          display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
          zIndex: 10,
        }}>
          <div style={{
            width: 7, height: 7, borderRadius: "50%",
            background: connected ? "#22c55e" : "#ef4444",
            boxShadow: connected ? "0 0 8px #22c55e" : "none",
          }} />
          {connected ? "En línea" : "Reconectando..."}
        </div>
      </div>

      {/* ── CHAT (derecha, 50%) ── */}
      <div style={{ width: "50%", flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#070b12" }}>

        {/* Header del chat */}
        <div style={{
          padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: vfs(14) }}>Felipe</div>
            <div style={{ fontSize: vfs(11), color: "#64748b" }}>CRM Systems Engineer</div>
          </div>
          <button
            onClick={onOpenCreds}
            title="Configurar credenciales de servicios"
            style={{
              background: "rgba(61,111,255,0.15)", color: BLUE,
              border: "1px solid rgba(61,111,255,0.3)", borderRadius: 7,
              padding: "5px 12px", fontSize: vfs(11), fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
            }}
          >
            🔐 Credenciales
          </button>
        </div>

        {/* Mensajes */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
          {chat.map((m, i) => (
            <Burbuja key={i} msg={m} isDark={true} onAction={executeAction} onActionWithParams={startActionWithParams} />
          ))}
          {chatLoading && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
              <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 14px", fontSize: vfs(12), color: "#94a3b8" }}>
                ⏳ Felipe está pensando...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && input.trim()) { sendMessage(input.trim()); setInput(""); } }}
            placeholder="Escribe a Felipe..."
            style={{
              flex: 1, padding: "9px 14px", borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.05)", color: "#e2e8f0",
              fontSize: vfs(13), outline: "none",
            }}
          />
          <button
            onClick={() => { if (input.trim()) { sendMessage(input.trim()); setInput(""); } }}
            disabled={chatLoading || !input.trim()}
            style={{
              background: BLUE, color: "#fff", border: "none", borderRadius: 10,
              width: 42, cursor: "pointer", fontSize: vfs(18), fontWeight: 700, flexShrink: 0,
              opacity: chatLoading || !input.trim() ? 0.5 : 1,
            }}
          >→</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD PRINCIPAL
// ══════════════════════════════════════════════════════════════

export default function FelipeDashboard() {
  useTheme(); // suscripción al contexto (necesaria para evitar errores de hooks)
  const isDark = true; // Felipe siempre oscuro — página de ingeniería
  const { usuario } = useAuth();
  const userName = usuario?.nombre || usuario?.email || "equipo";

  const {
    internals, externals, visores, logs, tickets, fixes, alerts, chat,
    connected, countdown, chatLoading,
    alertCount, warnCount,
    sendMessage, executeAction, startActionWithParams, pendingAction, setPendingAction, actionLoading,
    approveFix, rejectFix,
  } = useFelipe(userName);

  const [activeTab,    setActiveTab]    = useState("internals");
  const [drawerMon,    setDrawerMon]    = useState(null);
  const [logFilter,    setLogFilter]    = useState("all");
  const [ticketFilter, setTicketFilter] = useState("todos");
  const [showCreds,    setShowCreds]    = useState(false);
  const [credsLoading, setCredsLoading] = useState(false);

  // Colores base
  const bg      = isDark ? "#070b12" : "#f0f4f8";
  const surface = isDark ? "#0d1017" : "#ffffff";
  const border  = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)";
  const text     = isDark ? "#e2e8f0" : "#0f172a";
  const muted    = isDark ? "#475569" : "#94a3b8";

  // Guardar todas las credenciales de servicios de una vez
  const saveAllCredentials = useCallback(async (credsList) => {
    setCredsLoading(true);
    const token = localStorage.getItem("token");
    const results = [];
    for (const cred of credsList) {
      try {
        const res = await fetch("/api/felipe/actions/execute", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            action_id: "save_service_credentials",
            params: { service: cred.service, email: cred.email, password: cred.password },
          }),
        });
        const data = await res.json();
        results.push(data.ok ? `✅ ${cred.service}` : `❌ ${cred.service}: ${data.message}`);
      } catch {
        results.push(`❌ ${cred.service}: error de red`);
      }
    }
    setShowCreds(false);
    setCredsLoading(false);
    sendMessage(`He guardado las credenciales:\n${results.join("\n")}\n¿Todo correcto? Ya puedo actuar de forma autónoma cuando sea necesario.`);
  }, [sendMessage]);

  // Investigar un monitor con Felipe
  const investigateMonitor = useCallback(async (mon) => {
    const msg = `Investiga el servicio "${mon.label}": estado ${mon.status}, valor "${mon.value}", detalle: "${mon.detail}". Diagnóstica la causa y dime qué piensas hacer.`;
    await sendMessage(msg);
    setActiveTab("chat");
  }, [sendMessage]);

  // Acción directa desde monitor card
  const handleCardFix = useCallback((mon, fixAction) => {
    if (fixAction.params?.length) {
      startActionWithParams({ ...fixAction, monitorId: mon.id });
    } else {
      executeAction(fixAction.id, {});
    }
    setActiveTab("chat");
  }, [executeAction, startActionWithParams]);

  // Layout externo
  const extsWithFirmx = [...externals, FIRMX_CARD];

  // Filtros de logs
  const filteredLogs = logFilter === "all" ? logs : logs.filter(l => l.level === logFilter);
  const filteredTickets = ticketFilter === "todos" ? tickets : tickets.filter(t => t.status === ticketFilter);

  const internalErrors  = internals.filter(m => m.status === "error").length;
  const internalWarns   = internals.filter(m => m.status === "warn").length;
  const externalErrors  = extsWithFirmx.filter(m => m.status === "error").length;
  const externalWarns   = extsWithFirmx.filter(m => m.status === "warn").length;

  const TABS = [
    { id: "internals", label: "CRM Interno",  icon: "🖥️", badge: internalErrors + internalWarns > 0 ? internalErrors + internalWarns : null },
    { id: "externals", label: "Externos",     icon: "🌐", badge: externalErrors + externalWarns > 0 ? externalErrors + externalWarns : null },
    { id: "fixes",     label: "Fixes",        icon: "🔧", badge: fixes.length > 0 ? fixes.length : null },
    { id: "logs",      label: "Logs",         icon: "📋" },
    { id: "tickets",   label: "Tickets",      icon: "🎫" },
    { id: "chat",      label: "Felipe",       icon: "💬" },
  ];

  return (
    <div style={{
      minHeight: "calc(100vh - 60px)",
      background: bg, display: "flex", flexDirection: "column",
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      {/* Barra de estado + tabs */}
      <div style={{
        background: surface, borderBottom: `1px solid ${border}`,
        display: "flex", alignItems: "center", padding: "0 16px", gap: 2, flexShrink: 0,
        flexWrap: "wrap",
      }}>
        {/* Tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 2, flex: 1, paddingTop: 6, paddingBottom: 6 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              background: activeTab === t.id ? BLUE : "none",
              color: activeTab === t.id ? "#fff" : muted,
              border: "none", borderRadius: 6, padding: "5px 14px",
              fontSize: vfs(12), fontWeight: activeTab === t.id ? 700 : 500,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
              position: "relative", whiteSpace: "nowrap",
            }}>
              {t.icon} {t.label}
              {t.badge != null && (
                <span style={{
                  position: "absolute", top: 1, right: 1,
                  background: "#ef4444", color: "#fff",
                  fontSize: vfs(9), fontWeight: 800, borderRadius: 8,
                  minWidth: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 3px",
                }}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Indicadores + countdown */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: vfs(11) }}>
          {alertCount > 0 && <span style={{ color: "#ef4444", fontWeight: 700 }}>{alertCount} ERR</span>}
          {warnCount > 0  && <span style={{ color: "#eab308", fontWeight: 700 }}>{warnCount} WARN</span>}
          {fixes.length > 0 && (
            <span style={{ color: BLUE, cursor: "pointer", fontWeight: 700 }} onClick={() => setActiveTab("fixes")}>
              🔧 {fixes.length}
            </span>
          )}
          <span style={{ color: muted }}>⏱ {countdown}s</span>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: connected ? "#22c55e" : "#ef4444", boxShadow: connected ? "0 0 5px #22c55e" : "none" }} />
        </div>
      </div>

      {/* ── CONTENIDO ── */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>

        {/* CRM INTERNO */}
        {activeTab === "internals" && (
          <div style={{ height: "100%", overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: vfs(10), color: muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, flexShrink: 0 }}>
              Visores
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
              gridTemplateRows: "1fr 1fr",
              gap: 10,
              height: "calc(100vh - 150px)",
              minHeight: 280,
              flexShrink: 0,
            }}>
              {[{
                mon: visores?.whatsapp, col: 1,
              }, {
                mon: visores?.marketplace, col: 2,
              }, {
                mon: visores?.subidas, col: 3,
              }, {
                mon: internals.find((m) => m.id === "smtp"), col: 4, expand: true,
              }, {
                mon: visores?.marco, col: 5, expand: true,
              }].filter((slot) => slot.mon).map((slot) => (
                <div key={slot.mon.id} style={{ gridRow: 1, gridColumn: slot.col, minWidth: 0, minHeight: 0, height: "100%" }}>
                  <MonitorCard mon={{ ...slot.mon, expand: true }} isDark={isDark} onFix={handleCardFix} onHistory={setDrawerMon} />
                </div>
              ))}
              {!visores && (
                <div style={{ gridRow: 1, fontSize: vfs(12), color: muted }}>Cargando sesiones y MarketPlace…</div>
              )}
              <div style={{
                gridColumn: "1 / -1",
                gridRow: 2,
                minWidth: 0,
                minHeight: 0,
                height: "100%",
                display: "grid",
                gridTemplateRows: "minmax(0, 1fr)",
                gridAutoFlow: "column",
                gridAutoColumns: "calc((100% - 70px) / 8)",
                gap: 10,
                overflowX: "auto",
                overflowY: "hidden",
              }}>
                {internals.filter(mon => mon.id !== "photos" && mon.id !== "baileys" && mon.id !== "wizard" && mon.id !== "ana" && mon.id !== "wa_webhook" && mon.id !== "cron" && mon.id !== "jwt" && mon.id !== "smtp").map(mon => (
                  <div key={mon.id} style={{ minWidth: 0, minHeight: 0, height: "100%" }}>
                    <MonitorCard fill mon={mon} isDark={isDark} onFix={handleCardFix} onHistory={setDrawerMon} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SERVICIOS EXTERNOS */}
        {activeTab === "externals" && (
          <div style={{ height: "100%", overflow: "auto", padding: "14px 16px", display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: vfs(10), color: muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, flexShrink: 0 }}>
              Visores
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
              gridTemplateRows: "1fr 1fr",
              gap: 10,
              height: "calc(100vh - 150px)",
              minHeight: 280,
              flexShrink: 0,
            }}>
              {extsWithFirmx.slice(0, 6).map((mon, i) => (
                <div key={mon.id} style={{ gridRow: 1, gridColumn: i + 1, minWidth: 0, minHeight: 0, height: "100%" }}>
                  <MonitorCard fill mon={mon} isDark={isDark} onFix={handleCardFix} onHistory={setDrawerMon} />
                </div>
              ))}
              {extsWithFirmx.slice(6, 12).map((mon, i) => (
                <div key={mon.id} style={{ gridRow: 2, gridColumn: i + 1, minWidth: 0, minHeight: 0, height: "100%" }}>
                  <MonitorCard fill mon={mon} isDark={isDark} onFix={handleCardFix} onHistory={setDrawerMon} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FIXES PENDIENTES */}
        {activeTab === "fixes" && (
          <div style={{ height: "100%", overflow: "auto", padding: "16px 20px" }}>
            <div style={{ fontSize: vfs(13), fontWeight: 700, color: text, marginBottom: 12 }}>
              🔧 Fixes propuestos por Felipe
            </div>
            {fixes.length === 0 ? (
              <div style={{ textAlign: "center", color: muted, marginTop: 60, fontSize: vfs(13) }}>
                <div style={{ fontSize: vfs(36), marginBottom: 8 }}>✅</div>
                No hay fixes pendientes. Felipe tiene todo bajo control.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
                {fixes.map((fix, i) => (
                  <FixCard key={fix.fix_id || fix.id || i} fix={fix} isDark={isDark}
                    onApprove={approveFix} onReject={rejectFix} loading={actionLoading} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* LOGS */}
        {activeTab === "logs" && (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Filtros */}
            <div style={{ padding: "8px 16px", borderBottom: `1px solid ${border}`, display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: vfs(11), color: muted }}>Filtrar:</span>
              {["all", "error", "warn", "info"].map(f => (
                <button key={f} onClick={() => setLogFilter(f)} style={{
                  background: logFilter === f ? BLUE : "none",
                  color: logFilter === f ? "#fff" : muted,
                  border: `1px solid ${logFilter === f ? BLUE : border}`,
                  borderRadius: 5, padding: "2px 10px", fontSize: vfs(11), cursor: "pointer",
                  fontWeight: logFilter === f ? 700 : 400,
                }}>
                  {f === "all" ? "Todos" : f.toUpperCase()}
                </button>
              ))}
              <span style={{ fontSize: vfs(11), color: muted, marginLeft: "auto" }}>
                {filteredLogs.length} entradas
              </span>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "8px 16px" }}>
              {filteredLogs.map((l, i) => <LogRow key={i} log={l} isDark={isDark} />)}
              {filteredLogs.length === 0 && (
                <p style={{ color: muted, fontSize: vfs(12), textAlign: "center", marginTop: 40 }}>Sin logs</p>
              )}
            </div>
          </div>
        )}

        {/* TICKETS */}
        {activeTab === "tickets" && (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Filtros */}
            <div style={{ padding: "8px 16px", borderBottom: `1px solid ${border}`, display: "flex", gap: 6, flexShrink: 0 }}>
              {["todos", "abierto", "en proceso", "escalado", "cerrado"].map(f => (
                <button key={f} onClick={() => setTicketFilter(f)} style={{
                  background: ticketFilter === f ? BLUE : "none",
                  color: ticketFilter === f ? "#fff" : muted,
                  border: `1px solid ${ticketFilter === f ? BLUE : border}`,
                  borderRadius: 5, padding: "2px 10px", fontSize: vfs(11), cursor: "pointer",
                  textTransform: "capitalize",
                }}>
                  {f}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
              {filteredTickets.length === 0 ? (
                <p style={{ color: muted, fontSize: vfs(12), textAlign: "center", marginTop: 40 }}>Sin tickets</p>
              ) : filteredTickets.map(t => (
                <div key={t.id} style={{
                  background: surface, border: `1px solid ${border}`,
                  borderLeft: `3px solid ${t.status === "abierto" ? "#eab308" : t.status === "escalado" ? "#ef4444" : t.status === "cerrado" ? "#22c55e" : BLUE}`,
                  borderRadius: 8, padding: "10px 14px", marginBottom: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: vfs(11), fontWeight: 700, color: BLUE }}>{t.ticket_ref}</span>
                    <span style={{ fontSize: vfs(10), color: muted }}>{new Date(t.created_at).toLocaleDateString("es-ES")}</span>
                    <span style={{
                      fontSize: vfs(10), borderRadius: 4, padding: "1px 8px", fontWeight: 700,
                      background: t.status === "escalado" ? "rgba(239,68,68,0.12)" : t.status === "cerrado" ? "rgba(34,197,94,0.12)" : "rgba(234,179,8,0.12)",
                      color: t.status === "escalado" ? "#ef4444" : t.status === "cerrado" ? "#22c55e" : "#eab308",
                    }}>{t.status}</span>
                  </div>
                  <p style={{ fontSize: vfs(12), color: text, margin: 0 }}><b>{t.user_name}:</b> {t.message}</p>
                  {t.felipe_response && (
                    <p style={{ fontSize: vfs(11), color: muted, margin: "6px 0 0", borderTop: `1px solid ${border}`, paddingTop: 6 }}>
                      💬 {t.felipe_response.slice(0, 200)}{t.felipe_response.length > 200 ? "..." : ""}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CHAT */}
        {activeTab === "chat" && (
          <div style={{ height: "calc(100vh - 120px)", display: "flex", overflow: "hidden" }}>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <FelipeChat
                chat={chat} isDark={isDark} chatLoading={chatLoading}
                sendMessage={sendMessage} executeAction={executeAction}
                startActionWithParams={startActionWithParams}
                userName={userName} connected={connected}
                onOpenCreds={() => setShowCreds(true)}
              />
            </div>
            {/* Panel lateral fixes cuando hay pendientes */}
            {fixes.length > 0 && (
              <div style={{
                width: 270, flexShrink: 0,
                borderLeft: "1px solid rgba(255,255,255,0.07)",
                overflow: "auto", padding: "12px",
                display: "flex", flexDirection: "column", gap: 10,
                background: "#070b12",
              }}>
                <div style={{ fontSize: vfs(11), fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 1 }}>
                  🔧 Fixes pendientes
                </div>
                {fixes.map((fix, i) => (
                  <FixCard key={i} fix={fix} isDark={true}
                    onApprove={approveFix} onReject={rejectFix} loading={actionLoading} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── DRAWER INFERIOR (historial monitor) ── */}
      {drawerMon && (
        <HistoryDrawer
          mon={drawerMon} isDark={isDark}
          onClose={() => setDrawerMon(null)}
          onInvestigate={investigateMonitor}
        />
      )}

      {/* ── MODAL ACCIÓN CON PARÁMETROS ── */}
      {pendingAction && (
        <ActionModal
          action={pendingAction}
          onExecute={(params) => executeAction(pendingAction.id, params)}
          onClose={() => setPendingAction(null)}
          loading={actionLoading}
          isDark={isDark}
        />
      )}

      {/* ── MODAL CREDENCIALES DE SERVICIOS ── */}
      {showCreds && (
        <CredentialsModal
          isDark={isDark}
          loading={credsLoading}
          onClose={() => setShowCreds(false)}
          onSave={saveAllCredentials}
        />
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 3px; }
      `}</style>
    </div>
  );
}
